import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { ApiError, api, unwrap } from '../api/client.js';
import { errorMessage } from '../errors.js';
import { Button, Card, LinkButton, Notice, TextField } from './ui.js';

type Kind = 'slack' | 'webhook';
/** One-time links of a Telegram chat waiting for "Starten" (T-69). */
export interface TelegramLinks {
  channelId: string;
  connectUrl: string;
  connectGroupUrl: string;
}

/** Slack or an own system: its address and a name, added with one button (notifications.html). */
function AddForm({
  kind,
  busy,
  onAdd,
}: {
  kind: Kind;
  busy: boolean;
  onAdd: (url: string, label: string, done: () => void) => void;
}) {
  const { t } = useTranslation();
  const [url, setUrl] = useState('');
  const [label, setLabel] = useState('');
  const submit = (e: FormEvent) => {
    e.preventDefault();
    onAdd(url.trim(), label.trim(), () => {
      setUrl('');
      setLabel('');
    });
  };
  return (
    <form onSubmit={submit} noValidate>
      <TextField
        label={t(`chat.${kind}Url`)}
        hint={t(`chat.${kind}UrlHint`)}
        type="url"
        autoComplete="off"
        required
        placeholder={kind === 'slack' ? 'https://hooks.slack.com/services/…' : 'https://'}
        value={url}
        onChange={(e) => setUrl(e.target.value)}
      />
      <TextField
        label={t('chat.label')}
        optional
        maxLength={80}
        placeholder={kind === 'slack' ? '#website' : 'CRM'}
        value={label}
        onChange={(e) => setLabel(e.target.value)}
      />
      <Button type="submit" variant="secondary" size="sm" busy={busy}>
        {t(`chat.${kind}`)}
      </Button>
    </form>
  );
}

/**
 * Immediate alerts to Telegram, Slack or an own endpoint (T-69, notifications.html): one box per
 * channel. The recipients card lists every channel; this card adds them and shows the one-time data
 * (Telegram links, the webhook's signing secret).
 */
export function ChatChannelsCard({
  links,
  onLinks,
}: {
  links: TelegramLinks | undefined;
  onLinks: (links: TelegramLinks) => void;
}) {
  const { t } = useTranslation();
  const client = useQueryClient();
  const channels = useQuery({
    queryKey: ['alert-channels'],
    queryFn: () => unwrap(api.GET('/alert-channels')),
  });
  const refresh = () => client.invalidateQueries({ queryKey: ['alert-channels'] });
  const [secret, setSecret] = useState<string>();
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string>();
  // What a button brings up takes the focus: the button itself is gone, and a screen reader learns
  // of the links and the secret shown once (review of SP-UX10.6a).
  const linksBox = useRef<HTMLDivElement>(null);
  const secretBox = useRef<HTMLDivElement>(null);
  // Spent once the chat is connected (or gone): then a new chat can be connected again.
  const waiting = links
    ? (channels.data?.items.some((c) => c.id === links.channelId && !c.verified) ?? false)
    : false;
  const shownLinks = waiting ? links?.connectUrl : undefined;
  useEffect(() => linksBox.current?.focus(), [shownLinks]);
  useEffect(() => secretBox.current?.focus(), [secret]);

  const failed = (e: unknown) =>
    setError(
      e instanceof ApiError && e.code === 'PLAN_LIMIT_REACHED' && e.problem.limit?.max
        ? t('notifications.limitReached', { count: e.problem.limit.max })
        : errorMessage(e, t),
    );
  const telegram = useMutation({
    mutationFn: () => unwrap(api.POST('/alert-channels', { body: { type: 'telegram' } })),
    onSuccess: (c) => {
      // The new chat joins the list at once: its links show while the list reloads.
      client.setQueryData<typeof channels.data>(['alert-channels'], (old) =>
        old ? { ...old, items: [...old.items, c] } : old,
      );
      if (c.connectUrl && c.connectGroupUrl)
        onLinks({ channelId: c.id, connectUrl: c.connectUrl, connectGroupUrl: c.connectGroupUrl });
      void refresh();
    },
    onError: failed,
  });
  const add = useMutation({
    mutationFn: ({
      kind,
      url,
      label,
    }: {
      kind: Kind;
      url: string;
      label: string;
      done: () => void;
    }) =>
      unwrap(
        api.POST('/alert-channels', { body: { type: kind, url, ...(label ? { label } : {}) } }),
      ),
    onSuccess: (c, { done }) => {
      done();
      if (c.signingSecret) {
        setSecret(c.signingSecret);
        setCopied(false);
      }
      void refresh();
    },
    onError: failed,
  });

  const data = channels.data;
  if (!data) return null;
  const { chat } = data;
  const connected = data.items.find((c) => c.type === 'telegram' && c.verified);
  const adder = (kind: Kind) => (
    <AddForm
      kind={kind}
      busy={add.isPending && add.variables.kind === kind}
      onAdd={(url, label, done) => {
        setError(undefined);
        add.mutate({ kind, url, label, done });
      }}
    />
  );

  return (
    <Card>
      <h2>
        {t('chat.title')} <small>{t('chat.intro')}</small>
      </h2>
      {!chat.slack ? (
        <div className="upsell">
          <span>{t('chat.planNeeded')}</span>
          <LinkButton to="/billing" variant="secondary" size="sm">
            {t('chat.upgrade')}
          </LinkButton>
        </div>
      ) : (
        <div className="chan">
          {chat.telegram ? (
            <div>
              <h3>Telegram</h3>
              {connected ? <p>{t('chat.connected', { name: connected.target })}</p> : null}
              {links && waiting ? (
                <div ref={linksBox} tabIndex={-1} className="tg">
                  <p>{t('chat.telegramSteps')}</p>
                  <div className="tg-links">
                    <a href={links.connectUrl} target="_blank" rel="noreferrer">
                      {t('chat.telegramPrivate')}
                    </a>
                    <a href={links.connectGroupUrl} target="_blank" rel="noreferrer">
                      {t('chat.telegramGroup')}
                    </a>
                  </div>
                  <p className="hint">{t('chat.telegramHint')}</p>
                  <Button variant="secondary" size="sm" onClick={() => void refresh()}>
                    {t('chat.telegramDone')}
                  </Button>
                </div>
              ) : (
                // The links are made on demand: each one is a new chat waiting for "Starten".
                <Button
                  variant="secondary"
                  size="sm"
                  busy={telegram.isPending}
                  onClick={() => {
                    setError(undefined);
                    telegram.mutate();
                  }}
                >
                  {t('chat.telegram')}
                </Button>
              )}
            </div>
          ) : null}
          <div>
            <h3>Slack</h3>
            {adder('slack')}
          </div>
          <div>
            <h3>Webhook</h3>
            {!chat.webhook ? (
              <div className="upsell">
                <span>{t('chat.webhookPlan')}</span>
                <LinkButton to="/billing" variant="secondary" size="sm">
                  {t('chat.upgrade')}
                </LinkButton>
              </div>
            ) : secret ? (
              <div ref={secretBox} tabIndex={-1} className="secret">
                <b>{t('chat.secretTitle')}</b>
                <code>{secret}</code>
                <div className="acts">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      // Refused copying changes nothing: the secret stands above the button.
                      void navigator.clipboard?.writeText(secret).then(
                        () => setCopied(true),
                        () => undefined,
                      );
                    }}
                  >
                    {copied ? t('chat.copied') : t('chat.copy')}
                  </Button>
                </div>
                <span className="hint">{t('chat.secretHint')}</span>
              </div>
            ) : (
              adder('webhook')
            )}
          </div>
        </div>
      )}
      {error ? <Notice kind="error">{error}</Notice> : null}
    </Card>
  );
}
